import { useSelector } from 'react-redux';
import { useState, useCallback, useEffect, useRef } from 'react';
import { useDrop } from 'react-dnd';
import PropTypes from 'prop-types';
import { Add } from '@mui/icons-material';
import { Box, Stack, Drawer, Button } from '@mui/material';
import { toast } from 'react-toastify';
import { useStyles } from '../styles/sectionStyles';
import { SectionHeader, TaskCard } from '.';
import AddTaskForm from './AddTaskForm';
import { isAdmin, isStakeholder } from '../../../../utils/share';
import ProjectAddTask from '../../details/components/projectTaskList/ProjectAddTask';
import { updateTask, getTaskById } from '../../../../services/taskService';
import { getProjectById } from '../../../../services/projectService';
import { useProject } from '../../Layout/header/context';
import { Loader } from '../../../../components/loader';

const Section = ({ section, refetchSection, setLoading, runningTask }) => {
  const [{ isOver }, drop] = useDrop(() => ({
    accept: 'task',
    drop: (item) => addItemToSection(item),
    collect: (monitor) => ({
      isOver: !!monitor.isOver(),
    }),
  }));

  const classes = useStyles({ isOver });
  const [action, setAction] = useState(null); // 'create', 'update', null
  const [selected, setSelected] = useState(null); // task object
  const [showAddForm, setShowAddForm] = useState(false); // show inline add form
  const [isSectionLoading, setIsSectionLoading] = useState(false); // section loading state
  const [projectDetailsForAdmin, setProjectDetailsForAdmin] = useState(null);
  const [isDrawerLoading, setIsDrawerLoading] = useState(false);
  const sectionRef = useRef(null);

  const accountData = useSelector((state) => state?.login?.user);

  const { projectDetails, isEditPermission, projectRole } = useProject();
  const userIsAllowedToAddTask = isAdmin() || isStakeholder() || isEditPermission || projectDetails?.allowManualTask || projectRole !== 'Team Members';

  // Turn off section loading when section data changes
  useEffect(() => {
    if (isSectionLoading) {
      setIsSectionLoading(false);
    }
  }, [section?.tasks, isSectionLoading]);

  // Fetch project details for admin users (to get project members for assignee dropdown)
  useEffect(() => {
    const fetchProjectDetails = async () => {
      if (userIsAllowedToAddTask && projectDetails?.id) {
        try {
          const response = await getProjectById(projectDetails.id);
          setProjectDetailsForAdmin(response?.data?.data);
        } catch (error) {
          console.error('Error fetching project details for admin:', error);
        }
      }
    };

    fetchProjectDetails();
  }, [userIsAllowedToAddTask, projectDetails?.id]);

  const addItemToSection = useCallback(
    async (item) => {
      try {
        if (item?.sectionId === section?.id) return;
        await updateTask(item?.id, { sectionId: section?.id });
        refetchSection();
      } catch (error) {
        const errMsg =
          error?.response?.data?.message || 'An error occurred while updating the task. Please try again later.';
        toast.error(errMsg);
      }
    },
    [refetchSection, section?.id]
  );

  const handleAddTaskClick = useCallback(() => {
    if (!userIsAllowedToAddTask && !projectDetails?.allowManualTask) {
      toast.error('Access denied');
      return;
    }
    setShowAddForm((prev) => !prev);
  }, [projectRole, section?.id]);

  const handleTaskClick = useCallback(
    (task) => async () => {
      if (projectRole === 'Team Members' && task?.userId !== accountData?.id && !isAdmin()) return;

      // Open drawer immediately
      setAction('update');
      setSelected(null); // Clear previous selection

      // Show loading inside drawer
      setIsDrawerLoading(true);

      try {
        const response = await getTaskById(task.id);
        const taskData = response?.data?.data;
        if (taskData) {
          const updatedTaskData = {
            ...taskData,
            consumedHours: task.consumedHours,
          };
          setSelected(updatedTaskData);
        } else {
          toast.error('Task data not found');
        }
      } catch (error) {
        toast.error(error.message);
      } finally {
        setIsDrawerLoading(false);
      }
    },
    [accountData?.id, projectRole]
  );

  const handleDrawerClose = () => {
    setSelected(null);
    setAction(null);
    setIsDrawerLoading(false);
  };

  const handleAddFormCancel = () => {
    setShowAddForm(false);
  };

  const handleAddFormSubmit = () => {
    setShowAddForm(false);
    setIsSectionLoading(true);
    refetchSection(false); // Silent refetch without page loader
  };

  const createTaskDisabled = projectDetails?.allowManualTask === false && !isEditPermission;
  return (
    <Box ref={sectionRef} className={classes.section}>
      <SectionHeader onAddClick={handleAddTaskClick} section={section} refetchSection={refetchSection} />
      {isSectionLoading ? (
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            minHeight: '100px',
            p: 2,
          }}
        >
          <Loader size={40} />
        </Box>
      ) : (
        <Box ref={drop} className={classes?.taskStack} id={`scrollableDiv-${section?.id}`}>
          <Stack spacing={1}>
            {section?.tasks
              ?.filter((task) => task?.taskName?.trim()?.toLowerCase() !== 'not aware') // exclude "Not Aware"
              ?.map((task) => (
                <TaskCard
                  key={task?.id}
                  task={task}
                  onClick={handleTaskClick(task)}
                  setLoading={setLoading}
                  runningTask={runningTask}
                  refetchSection={refetchSection}
                  disabled={createTaskDisabled}
                />
              ))}

            {showAddForm && (
              <AddTaskForm
                sectionId={section?.id}
                projectId={projectDetails?.id}
                onCancel={handleAddFormCancel}
                onSubmit={handleAddFormSubmit}
                defaultAssignToSelf
                projectDetails={userIsAllowedToAddTask ? projectDetailsForAdmin : projectDetails}
                showAssigneeDropdown={userIsAllowedToAddTask && projectRole !== 'Team Members'}
              />
            )}
            {userIsAllowedToAddTask && !showAddForm && (
              <Button color="inherit" className={classes?.addTaskBtn} onClick={handleAddTaskClick} startIcon={<Add />}>
                Add Task
              </Button>
            )}
          </Stack>
        </Box>
      )}
      <Drawer PaperProps={{ sx: { width: '55%' } }} anchor="right" open={Boolean(action)} onClose={handleDrawerClose}>
        <Box sx={{ height: '100vh', display: 'flex', flexDirection: 'column' }}>
          {isDrawerLoading ? (
            <Box
              sx={{
                flex: 1,
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                flexDirection: 'column',
                gap: 2,
              }}
            >
              <Loader size={40} />
            </Box>
          ) : (
            <Box sx={{ height: '100%', overflow: 'auto', paddingBottom: '20px' }}>
              <ProjectAddTask
                sectionId={section?.id}
                selectedTask={selected}
                close={handleDrawerClose}
                action={action}
                onSubmit={refetchSection}
                hideProjectName
              />
            </Box>
          )}
        </Box>
      </Drawer>
    </Box>
  );
};

Section.propTypes = {
  section: PropTypes.object,
  refetchSection: PropTypes.func,
  setLoading: PropTypes.func,
  runningTask: PropTypes.object,
};

export default Section;
